/** This is the main entry file for the ChatBot iframe
 * - Setup parent window connection
 * - Get bot configuration from backend
 * - Render React app
 */
import 'custom-event-polyfill';
import 'url-polyfill';
import 'es6-promise/auto';
import 'isomorphic-fetch';
import 'intersection-observer';
import React from 'react';
import ReactDOM from 'react-dom';
import Penpal from 'penpal';
import * as Sentry from '@sentry/react';
import {
  CloudFunction,
  InitBotRequestBody,
  InitBotResponseBody,
  PageView,
} from '@tomis-tech/types';

import App from './App';
import platform from '../platform';
import { logError, Category, addBreadcrumb } from './sentry-iframe';
import { initialParentWidth, initialParentHeight, SITE_SLUG } from './consts';
import { BotParentPenpalMethods, NumberPair } from './types';
import { FIREBASE, initFirebase } from '../firebase';
import { invokeFunction } from '../cloud-functions';
import {
  setGoogleAnalyticsPageview,
  setGoogleAnalyticsUserId,
  trackGA4Event,
} from '../tracking/googleAnalytics';
import { deviceIdKey } from '../tomis-device-id/consts';
import { BotChildPenpalMethods } from './setupBot';
import { GA4Event } from '../tracking';
import { STORAGE } from './iframeStorage';
import { View, getIframePosition } from './view';

const renderEventName = 'render_app';
const renderEvent = new CustomEvent(renderEventName);

let parentWindowSize: NumberPair = [initialParentWidth, initialParentHeight];

try {
  STORAGE.tomisDeviceId = localStorage.getItem(deviceIdKey);
} catch (error) {
  // local storage may be disabled, and that's OK
}

/** Parent window page view data - set on initial load
 * before the React app is rendered. This means we can
 * safely use it anywhere in the app. Does not update,
 * unless a different page is loaded and the entire bot
 * loads again.
 *
 * Currently not compatible with SPAs. We are NOT
 * listening for browser events and updating this variable accordingly.
 *
 * In the future we may implement SPA support using
 * the Navigation API (currently experimental May 2023):
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API
 */
export let PARENT_PAGE_VIEW: PageView;

/** Before setting up React app:
 * - Load GA4 for internal tracking, including loading errors
 * - Load Sentry for error logging
 * - Ensure this page is loaded as an iframe
 * - Ensure iframe has access to session storage
 */
import('./initialize')
  .then(initFirebase)
  .then(() => {
    const connectionToParent = Penpal.connectToParent({
      methods: {
        updateParentWindowSize(newParentWindowSize: NumberPair) {
          parentWindowSize = newParentWindowSize;
          window.dispatchEvent(renderEvent);
        },
        setIframeStorage(items) {
          STORAGE.conversationId = items.conversationId;
          STORAGE.webSessionId = items.webSessionId;
          STORAGE.tomisDeviceId = items.tomisDeviceId;
          window.dispatchEvent(renderEvent);
        },
      } as BotChildPenpalMethods,
    });

    connectionToParent.promise
      .then(
        async (parent: BotParentPenpalMethods) => {
          if (
            !parent ||
            !parent.getCurrentPageView ||
            !parent.getGa4ClientId ||
            !parent.setConversationIdSessionStorage
          ) {
            // unclear why this would happen, but it's a fatal error
            trackGA4Event(GA4Event.LoadFailed, {
              failure_reason: 'No parent window connection',
            });
            return console.warn('TOMIS ChatBot cannot load - no parent window');
          }

          PARENT_PAGE_VIEW = await parent.getCurrentPageView();
          setGoogleAnalyticsPageview(PARENT_PAGE_VIEW);
          const ga4ClientId = await parent.getGa4ClientId();

          if (!STORAGE.tomisDeviceId || !STORAGE.webSessionId) {
            // unclear why this would happen, but it's a fatal error
            trackGA4Event(GA4Event.LoadFailed, {
              failure_reason: 'No TOMIS Device ID',
            });
            return console.warn(
              'TOMIS ChatBot cannot load without TOMIS Device ID'
            );
          }

          setGoogleAnalyticsUserId(STORAGE.tomisDeviceId);

          const { data, errorMessage, status } = await initBot({
            tomisDeviceId: STORAGE.tomisDeviceId,
            pageView: PARENT_PAGE_VIEW,
            ga4ClientId,
            webSessionId: STORAGE.webSessionId,
            conversationId: STORAGE.conversationId,
          });

          if (status === 'failure' || status === 'prevented') {
            trackGA4Event(
              status == 'failure'
                ? GA4Event.LoadFailed
                : GA4Event.LoadPrevented,
              {
                failure_reason: errorMessage,
              }
            );
            /** Backend handles error logging. Here, log to console
             * and gracefully return to stop ChatBot from loading. */
            return console.warn(errorMessage);
          }

          const { conversationId, firebaseUid, firebaseUserToken, site } = data;

          async function showChatBot() {
            const { isMobile } = await parent.getParentWindowDimensions();

            const position = getIframePosition(
              View.ClosedWithPreview,
              isMobile,
              site.botConfig.styleOptions
            );

            await parent.setPosition(position, View.ClosedWithPreview);

            // Sign in to Firebase, errors are fatal
            const userCredential = await FIREBASE.auth.signInWithCustomToken(
              firebaseUserToken
            );
            addBreadcrumb({
              message: `Firebase user logged in: ${userCredential.user.uid}`,
            });

            // Update conversation id w/ new value from backend
            STORAGE.conversationId = conversationId;
            await parent.setConversationIdSessionStorage(conversationId);

            // create react app
            const appRoot = document.createElement('div');
            appRoot.id = 'app-root';
            document.body.appendChild(appRoot);

            function renderApp() {
              if (PARENT_PAGE_VIEW && firebaseUid) {
                ReactDOM.render(
                  <App
                    parentWindowMethods={parent}
                    parentWindowSize={parentWindowSize}
                    firebaseUid={firebaseUid}
                    conversationId={conversationId}
                    site={site}
                  />,
                  appRoot
                );
              }
            }

            // Listen for changes to parent window size
            window.addEventListener(renderEventName, renderApp);

            // Hot reloading for local development
            if (module.hot) module.hot.accept(renderApp);

            // Render React app for first time
            renderApp();
          }

          const loadingDelay = site.botConfig.loadingDelay || 0;

          if (loadingDelay) {
            setDelay(showChatBot, loadingDelay);
          } else {
            showChatBot();
          }
        },
        err => {
          logError(err, {
            category: Category.IFrame,
            message: 'Error connecting to parent window - cannot setup chatbot',
            level: 'fatal',
            type: 'error',
          });
          trackGA4Event(GA4Event.LoadFailed, {
            failure_reason: 'Error connecting to parent window',
          });

          if (connectionToParent && connectionToParent.destroy) {
            // remove iframe from DOM
            connectionToParent.destroy();
          }
        }
      )
      .catch(e => {
        const errorMessage =
          typeof e === 'object' && e.message
            ? e.message
            : 'Error setting up react app in chatbot iframe';
        logError(e, {
          message: 'Error setting up chatbot iframe',
          level: 'fatal',
        });
        trackGA4Event(
          GA4Event.LoadFailed,
          { failure_reason: errorMessage },
          true
        );
        if (window.frameElement) {
          // remove iframe from DOM
          window.frameElement.remove();
        }
      });
  })
  .catch(e => {
    const errorMessage =
      typeof e === 'object' && e.message
        ? e.message
        : 'TOMIS ChatBot failed to load on this page';
    trackGA4Event(GA4Event.LoadFailed, { failure_reason: errorMessage }, true);
    console.warn('TOMIS ChatBot failed to load on this page');
    console.error(e);
  });

/** Async call to cloud function to get bot configuration,
 * create conversation (if necessary), and get firebase user token
 *
 * ### Error Codes
 * - 4xx indicate that chatbot should not load (unauthorized domain, bad request, etc.)
 * - 5xx indicates that chatbot should load, but server error happened
 *
 * We need to handle these differently to improve GA4 reporting */
async function initBot({
  tomisDeviceId,
  pageView,
  ga4ClientId,
  webSessionId,
  conversationId,
}: {
  tomisDeviceId: string;
  webSessionId: string;
  pageView: PageView;
  conversationId?: string;
  ga4ClientId?: string;
}): Promise<{
  data?: InitBotResponseBody;
  errorMessage?: string;
  status: 'success' | 'failure' | 'prevented';
}> {
  const requestBody: InitBotRequestBody = {
    siteSlug: SITE_SLUG,
    webSessionId,
    tomisDeviceId,
    platform,
    pageView,
    conversationId: conversationId || undefined,
    ga4ClientId: ga4ClientId || undefined,
  };

  try {
    const [body, response] = await invokeFunction<InitBotResponseBody>({
      name: CloudFunction.Api,
      body: requestBody,
      fullUrl: 'api/webClient/initialize',
      logToSentry: false,
    });

    if (response.ok) {
      try {
        const features = (body.site.features || []).sort().join(', ');
        Sentry.setTag('conversationId', body.conversationId);
        Sentry.setTag('webSessionId', body.webSessionId);
        Sentry.setTag('tomisDeviceId', body.tomisDeviceId);
        Sentry.setTag('hostname', pageView.page.url.hostname);
        Sentry.setTag('path', pageView.page.url.pathname);
        Sentry.setTag('siteSlug', SITE_SLUG);
        Sentry.setTag('feature', features);
      } catch (error) {
        console.warn('Error setting Sentry scope');
      }
      return { data: body, status: 'success' };
    } else if (response.status >= 500) {
      // @ts-expect-error technical error occurred
      const errorMessage = body.error || 'Error initializing TOMIS ChatBot';
      return { errorMessage, status: 'failure' };
    } else {
      // @ts-expect-error chatbot purposefully prevented from loading by api
      const errorMessage = body.error || 'Error initializing TOMIS ChatBot';
      return { errorMessage, status: 'prevented' };
    }
  } catch (e) {
    // error already logged by invokeFunction
    const errorMessage =
      typeof e === 'object' && e.message
        ? e.message
        : 'Error initializing TOMIS ChatBot';
    return {
      errorMessage,
      status: 'failure',
    };
  }
}

/** Perform a callback function after X total seconds of being on the website.
 *
 * This function takes into consideration the time the user has been on the website (multiple pageviews)
 */
function setDelay(
  /** Function to execute after X seconds on the site */
  callback: () => any,
  /** Number of seconds since web session began before callback is executed */
  seconds: number
): void {
  try {
    let webSessionStartTime = sessionStorage.getItem(
      `${SITE_SLUG}_webSessionStartTime`
    );

    if (!webSessionStartTime) {
      webSessionStartTime = String(Date.now());
      sessionStorage.setItem(
        `${SITE_SLUG}_webSessionStartTime`,
        webSessionStartTime
      );
    }

    const now = Date.now();
    const elapsedMilliseconds = now - Number(webSessionStartTime);
    const delayMilliseconds = seconds * 1000;

    if (elapsedMilliseconds >= delayMilliseconds) {
      console.warn(
        `TOMIS ChatBot loading delay of ${seconds} seconds reached, loading now...`
      );
      callback();
    } else {
      console.warn(
        `TOMIS ChatBot loading delay remaining time: ${delayMilliseconds -
          elapsedMilliseconds} milliseconds`
      );
      const remainingMilliseconds = delayMilliseconds - elapsedMilliseconds;
      setTimeout(() => {
        callback();
        console.warn(
          `TOMIS ChatBot loading delay of ${seconds} seconds reached, loading now...`
        );
      }, remainingMilliseconds);
    }
  } catch (error) {
    logError(error, { message: 'Error setting delay for callback' });
    callback();
  }
}
